/* eslint-disable no-plusplus, @typescript-eslint/await-thenable, @typescript-eslint/no-floating-promises */
import { useState, useCallback, useRef, useEffect } from 'react';
import { GaussianBlurBackgroundProcessor, Pipeline } from '@twilio/video-processors';
import { safeRemoveProcessor } from '../utils/videoEffectUtils';
import { BlurEffect } from '../videoEffectTypes';
import { VideoEffectStore } from '../store/videoEffectModel';

const BLUR_LIGHT_FILTER_RADIUS = 6;
const BLUR_LIGHT_MASK_RADIUS = 3;

const BLUR_HEAVY_FILTER_RADIUS = 20;
const BLUR_HEAVY_MASK_RADIUS = 0;

export const useBlurEffect = (assetsPath: string) => {
  const [isApplying, setIsApplying] = useState(false);
  const isVideoFrameSupported = VideoEffectStore.useStoreState((state) => state.isVideoFrameSupported);
  const setActiveBlurEffect = VideoEffectStore.useStoreActions((state) => state.setActiveBlurEffect);
  const videoTrack = VideoEffectStore.useStoreState((state) => state.localVideoTrack);

  const processorRef = useRef<GaussianBlurBackgroundProcessor | null>(null);

  const processorConfigRef = useRef<{
    assetsPath: string;
    pipeline: Pipeline;
    debounce: boolean;
  } | null>(null);

  const requestIdRef = useRef(0);

  const removeCurrentProcessor = useCallback(async () => {
    // istanbul ignore next
    if (!videoTrack) return;
    await safeRemoveProcessor(videoTrack, processorRef.current);
    processorRef.current = null;
    processorConfigRef.current = null;
  }, [videoTrack]);

  const applyEffect = useCallback(
    async (effectType: BlurEffect, options?: { pipeline?: Pipeline; debounce?: boolean }) => {
      if (!videoTrack || !isVideoFrameSupported) return;

      if (!videoTrack.dimensions?.width || !videoTrack.dimensions?.height) {
        console.warn('Camera dimensions are uninitialized.');
        return;
      }

      // istanbul ignore next
      if (isApplying) return;

      setIsApplying(true);
      const requestId = ++requestIdRef.current;

      try {
        if (effectType === 'none') {
          await removeCurrentProcessor();
          setActiveBlurEffect('none');
          return;
        }

        const isHeavy = effectType === 'blur-heavy';
        const blurFilterRadius = isHeavy ? BLUR_HEAVY_FILTER_RADIUS : BLUR_LIGHT_FILTER_RADIUS;
        const maskBlurRadius = isHeavy ? BLUR_HEAVY_MASK_RADIUS : BLUR_LIGHT_MASK_RADIUS;

        const pipeline = options?.pipeline ?? Pipeline.Canvas2D;
        const debounce = options?.debounce ?? true;

        const needsNewProcessor =
          !processorRef.current ||
          processorConfigRef.current?.assetsPath !== assetsPath ||
          processorConfigRef.current?.pipeline !== pipeline ||
          processorConfigRef.current?.debounce !== debounce;

        if (needsNewProcessor) {
          await removeCurrentProcessor();

          const processor = new GaussianBlurBackgroundProcessor({
            assetsPath,
            blurFilterRadius,
            maskBlurRadius,
            pipeline,
            debounce,
          });

          await processor.loadModel();
          if (requestId !== requestIdRef.current) return;

          // DYNAMIC ALIGNMENT FIX: Map Twilio buffer contexts directly to the ML pipeline engine choice
          const isCanvas2D = pipeline === Pipeline.Canvas2D;
          await videoTrack.addProcessor(processor, {
            inputFrameBufferType: isCanvas2D ? 'canvas' : 'video',
            outputFrameBufferContextType: isCanvas2D ? '2d' : 'webgl2',
          });

          processorRef.current = processor;
          processorConfigRef.current = { assetsPath, pipeline, debounce };
        } else {
          // Live transition optimization layout when contexts remain aligned
          const processor = processorRef.current!;
          processor.blurFilterRadius = blurFilterRadius;
          processor.maskBlurRadius = maskBlurRadius;
        }

        setActiveBlurEffect(effectType);
      } catch (error) {
        console.error('Failed to apply blur effect:', error);
        await removeCurrentProcessor();
        setActiveBlurEffect('none');
      } finally {
        if (requestId === requestIdRef.current) {
          setIsApplying(false);
        }
      }
    },
    [assetsPath, isApplying, isVideoFrameSupported, removeCurrentProcessor, setActiveBlurEffect, videoTrack],
  );

  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
      if (videoTrack && processorRef.current) {
        safeRemoveProcessor(videoTrack, processorRef.current);
      }
      processorRef.current = null;
      processorConfigRef.current = null;
    };
  }, [videoTrack]);

  return {
    applyEffect,
    isApplying,
  };
};
